import { createSlice } from "@reduxjs/toolkit";
import {
	getConfigRequest,
	getMatchesRequest,
	getMatchSummaryRequest,
	getTemplateRequest,
	getUserPredictionsRequest,
	makePredictionRequest,
} from "../../api/predictorService";
import { getCurrentGroup } from "../../fragments/PredictionsFragment/PredictionsHelpers";

const predictorSlice = createSlice({
	name: "Predictor",
	initialState: {
		matches: [],
		markets: [],
		predictions: [],
		groups: [],
		summary: [],
		rules: null,
	},
	reducers: {
		setMatches(state, action) {
			state.matches = action.payload;
		},
		setMarkets(state, action) {
			state.markets = action.payload;
		},
		setGroups(state, action) {
			state.groups = action.payload;
		},
		setUserPredictions(state, action) {
			state.predictions = action.payload;
		},
		updateUserPrediction(state, action) {
			if (state.predictions.filter((prediction) => prediction.id === action.payload.id).length === 0) {
				state.predictions = [...state.predictions, action.payload];
			} else {
				state.predictions = state.predictions.map((prediction) => {
					return prediction.id === action.payload.id ? action.payload : prediction;
				});
			}
		},
		setMatchesSummary(state, action) {
			state.summary = action.payload;
		},
		setRules(state, action) {
			state.rules = action.payload;
		},
	},
});

export const { setMatches, setMarkets, setUserPredictions, updateUserPrediction, setMatchesSummary, setRules, setGroups } = predictorSlice.actions;

export const getMatchesInfoRequest = (fansUnitedSDK) => async (dispatch) => {
	try {
		const templateResponse = await getTemplateRequest(fansUnitedSDK);
		if (!templateResponse) return;
		dispatch(setGroups(templateResponse.groups));
		dispatch(setRules(templateResponse.rules));

		const matchIds = templateResponse.matchIds;
		const competitionIds = templateResponse.competitionIds ?? [];

		const configResponse = await getConfigRequest(fansUnitedSDK);
		if (!configResponse) return;

		const markets = templateResponse.markets.map((market) => {
			return {
				type: market,
				points: configResponse.rewards.points.predictor.find((reward) => reward.id === market)?.points,
			};
		});
		dispatch(setMarkets(markets));

		// Get current group to fetch predictions only for current round matches
		const currentGroup = getCurrentGroup(templateResponse.groups);
		let currentGroupMatchIds = [];

		if (currentGroup && currentGroup.filters) {
			// If the group has specific matchIds, use those
			if (currentGroup.filters.matchIds && currentGroup.filters.matchIds.length > 0) {
				currentGroupMatchIds = currentGroup.filters.matchIds.filter((id) => id && id.trim() !== "");
			}
		}

		// Ensure we don't exceed the API limit of 30 match IDs
		if (currentGroupMatchIds.length > 30) {
			console.warn(`Current group has ${currentGroupMatchIds.length} matches, limiting to first 30 to avoid API error`);
			currentGroupMatchIds = currentGroupMatchIds.slice(0, 30);
		}

		// Fetch user predictions only for current group matches (or all if no current group)
		const userPredictionsResponse = await getUserPredictionsRequest(fansUnitedSDK, currentGroupMatchIds.length > 0 ? currentGroupMatchIds : null);
		dispatch(setUserPredictions(userPredictionsResponse?.data ?? []));

		// The /v1/matches endpoint caps responses around 200, so batch matchIds in chunks.
		const allMatchIds = matchIds ?? [];
		const MATCHES_BATCH_SIZE = 100;
		const batches = [];
		for (let i = 0; i < allMatchIds.length; i += MATCHES_BATCH_SIZE) {
			batches.push(allMatchIds.slice(i, i + MATCHES_BATCH_SIZE));
		}

		const batchResponses = await Promise.all(
			batches.map((batchIds) =>
				getMatchesRequest(fansUnitedSDK, competitionIds[0] ?? null, batchIds, templateResponse.fromDate, templateResponse.toDate),
			),
		);

		const allMatches = batchResponses.flatMap((res) => res?.data ?? []);

		if (allMatches.length === 0) return;
		dispatch(setMatches(allMatches));

		const matchesSummaryResponse = await Promise.all(allMatches.map(async (match) => await getMatchSummaryRequest(fansUnitedSDK, match.id)));
		if (!matchesSummaryResponse) return;
		dispatch(setMatchesSummary(matchesSummaryResponse));
	} catch (error) {
		throw error;
	}
};

export const createPredictionRequest = (fansUnitedSDK, matchId, market, prediction, playerId) => async (dispatch) => {
	try {
		const predictionResponse = await makePredictionRequest(fansUnitedSDK, matchId, market, prediction, playerId);
		if (!predictionResponse) return;
		dispatch(updateUserPrediction(predictionResponse));
	} catch (error) {
		throw error;
	}
};

export default predictorSlice.reducer;
